import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import { useAuthStore } from '../../stores/authStore';
import { getGrntOauthProviders } from '../../apis/api/public';
import './LoginPage.css';

interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  agreedToTerms?: string;
}

interface TosConfirmationPayload {
  tos_version?: string;
  tosVersion?: string;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading } = useAuthStore();

  // 从 URL query 读取 ProtectedRoute 传递的重定向目标
  const redirectTo = searchParams.get('redirect') || '/home';

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // OAuth 提供方可用状态
  const [oauthProviders, setOauthProviders] = useState<{
    googleEnabled: boolean;
    githubEnabled: boolean;
  }>({ googleEnabled: false, githubEnabled: false });

  // 页面加载时获取 OAuth 提供方配置
  useEffect(() => {
    getGrntOauthProviders()
      .then(result => {
        if (result.data) {
          setOauthProviders(result.data);
        }
      })
      .catch(() => {
        // 获取失败时不展示 SSO 按钮，忽略错误
      });
  }, []);

  // SSO 按钮点击处理：跳转到后端 OAuth 授权
  const handleOAuthLogin = (provider: string) => {
    // 优先从顶层 searchParams 获取邀请码
    let inviteCode = searchParams.get('inviteCode') || searchParams.get('invite_code');

    // 顶层没有时，尝试从 redirect 参数中解析（ProtectedRoute 会将原始路径编码到 redirect 中）
    if (!inviteCode) {
      const redirectParam = searchParams.get('redirect') || '';
      if (redirectParam) {
        const redirectUrl = new URL(redirectParam, window.location.origin);
        inviteCode =
          redirectUrl.searchParams.get('inviteCode') || redirectUrl.searchParams.get('invite_code');
      }
    }

    if (!inviteCode) {
      window.location.href = `/api/grnt/oauth/${provider}`;
      return;
    }

    const query = new URLSearchParams({ invite_code: inviteCode });
    window.location.href = `/api/grnt/oauth/${provider}?${query.toString()}`;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Please enter your password';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to the terms to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!validateForm()) {
      return;
    }

    let result = await login(formData.email, formData.password);

    if (!result.success && Number(result.code) === 46001) {
      const payloadData = result.data;
      const payload: TosConfirmationPayload =
        payloadData && typeof payloadData === 'object' ? payloadData : {};
      const tosVersion = payload.tos_version || payload.tosVersion || '';
      const confirmMessage = tosVersion
        ? `Terms of Service has been updated (${tosVersion}). Please confirm to continue.`
        : 'Terms of Service has been updated. Please confirm to continue.';

      // Use Modal.confirm from Ant Design
      const hasConfirmed = await new Promise<boolean>(resolve => {
        Modal.confirm({
          title: 'Terms of Service',
          content: confirmMessage,
          okText: 'Confirm',
          cancelText: 'Cancel',
          onOk() {
            resolve(true);
          },
          onCancel() {
            resolve(false);
          },
        });
      });

      if (!hasConfirmed) {
        setLoginError('Please confirm the latest Terms of Service to continue');
        return;
      }
      result = await login(formData.email, formData.password, tosVersion);
    }

    if (result.success) {
      navigate(redirectTo, { replace: true });
    } else {
      setLoginError(result.message || 'Login failed');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    // Clear login error on change
    if (loginError) {
      setLoginError('');
    }
  };

  return (
    <div className="login-page">
      {/* Background */}
      <div className="tech-bg">
        <div className="grid-overlay"></div>
        <div className="glow-orbs">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
          <div className="orb orb-4"></div>
        </div>
        <div className="scan-line"></div>
      </div>

      <div className="login-container">
        {/* Login card */}
        <div className="login-card">
          {/* Logo */}
          <div className="login-logo">
            <img src="/pomex-logo.png" alt="Pomex" className="logo-img" />
          </div>

          {/* Header */}
          <div className="login-header">
            <h2 className="login-title">Welcome back</h2>
            <p className="login-subtitle">Sign in to your account</p>
          </div>

          {/* Login form */}
          <form className="login-form" onSubmit={handleSubmit}>
            {/* Email */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className={`form-control with-icon ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isLoading}
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control with-icon ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                      <line x1="2" x2="22" y1="2" y2="22" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <span className="form-error">{errors.password}</span>}
            </div>

            {/* Terms and Privacy checkbox */}
            <div className="form-group terms-group">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={e => {
                    setAgreedToTerms(e.target.checked);
                    if (errors.agreedToTerms) {
                      setErrors(prev => ({ ...prev, agreedToTerms: undefined }));
                    }
                  }}
                  disabled={isLoading}
                />
                <span className="terms-text">
                  I agree to the{' '}
                  <a
                    href="https://www.pomex.ai/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="terms-link"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.pomex.ai/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="terms-link"
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
              {errors.agreedToTerms && <span className="form-error">{errors.agreedToTerms}</span>}
            </div>

            {/* Error message */}
            {loginError && (
              <div className="login-error">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" x2="12" y1="8" y2="12" />
                  <line x1="12" x2="12.01" y1="16" y2="16" />
                </svg>
                <span>{loginError}</span>
              </div>
            )}

            {/* Submit button */}
            <button type="submit" className="btn btn-primary btn-login" disabled={isLoading}>
              {isLoading ? (
                <>
                  <svg
                    className="spinner"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" />
                  </svg>
                  Signing in...
                </>
              ) : (
                <>Sign In</>
              )}
            </button>
          </form>

          {/* SSO 分隔线和按钮 */}
          {(oauthProviders.googleEnabled || oauthProviders.githubEnabled) && (
            <div className="sso-section">
              {/* 分隔线 */}
              <div className="sso-divider">
                <div className="sso-divider-line"></div>
                <span className="sso-divider-text">or</span>
                <div className="sso-divider-line"></div>
              </div>

              {/* Google 登录按钮 */}
              {oauthProviders.googleEnabled && (
                <button
                  type="button"
                  className="btn btn-sso btn-google"
                  onClick={() => handleOAuthLogin('google')}
                  disabled={isLoading}
                >
                  <svg className="sso-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Sign in with Google
                </button>
              )}

              {/* GitHub 登录按钮 */}
              {oauthProviders.githubEnabled && (
                <button
                  type="button"
                  className="btn btn-sso btn-github"
                  onClick={() => handleOAuthLogin('github')}
                  disabled={isLoading}
                >
                  <svg
                    className="sso-icon"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="currentColor"
                  >
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                  Sign in with GitHub
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
